import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Card } from "primereact/card";
import { getProducts, updateProductList, getProductList } from "../../store/AsyncMethods/ProductMethod";
import { toast } from "react-toastify";

export default function ProductSelectionMain() {
  const dispatch = useDispatch();
  const [textName, setTextName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  
  const { products = [], loading, productList } = useSelector((state) => state.ProductReducer);

  useEffect(() => {
    dispatch(getProducts());
    dispatch(getProductList());
  }, [dispatch]);

  useEffect(() => {
    if(productList){
      setSelectedProducts(productList?.list_array || []);
      setTextName(productList?.list_text || "");
    }
  }, [productList]);
 

  // Filter products based on search term
  const filteredProducts = (products || []).filter(product =>
    product?.title?.toLowerCase().includes((searchTerm || "").toLowerCase()) ||
    product?.description?.toLowerCase().includes((searchTerm || "").toLowerCase()) ||
    product?.category?.toLowerCase().includes((searchTerm || "").toLowerCase())
  );

  // Handle individual product selection
  const handleProductSelection = (productId, isChecked) => {
    if (isChecked) {
      setSelectedProducts(prev => [...(prev || []), productId]);
    } else {
      setSelectedProducts(prev => (prev || []).filter(id => id !== productId));
    }
  };

  // Handle select all products
  const handleSelectAll = () => {
    const currentSelected = selectedProducts || [];
    const filtered = filteredProducts || [];
    
    if (currentSelected.length === filtered.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filtered.map(product => product?.id).filter(id => id !== undefined));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const trimmedTextName = (textName || "").trim();
    
    if (!trimmedTextName) {
      toast.error("Please enter a text name");
      return;
    }

    if ((selectedProducts || []).length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    const submissionData = {
      textName: trimmedTextName,
      selectedProductIds: selectedProducts || []
    };

     dispatch(updateProductList(submissionData))
   
  };

  return (
    <div className="p-4 bg-gray-50 min-h-screen">
      <div className="mb-4">
        <p className="text-sm text-gray-600">Home &gt; Product Selection</p>
      </div>

      <Card className="mb-4">
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Selection Details</h3>
          
          {/* Text Input */}
          <div className="mb-4">
            <label htmlFor="textName" className="block text-sm font-medium text-gray-700 mb-2">
              Text Name <span className="text-red-500">*</span>
            </label>
            <InputText
              id="textName"
              value={textName || ""}
              onChange={(e) => setTextName(e.target.value || "")}
              placeholder="Enter text name..."
              className="w-full"
            />
          </div>

          {/* Search Bar */}
          <div className="mb-4">
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
              Search Products
            </label>
            <InputText
              id="search"
              value={searchTerm || ""}
              onChange={(e) => setSearchTerm(e.target.value || "")}
              placeholder="Search by name, description, or category..."
              className="w-full"
            />
          </div>

          {/* Select All Checkbox */}
          <div className="mb-4 flex items-center gap-2">
            <Checkbox
              checked={(selectedProducts || []).length === (filteredProducts || []).length && (filteredProducts || []).length > 0}
              onChange={handleSelectAll}
            />
            <label className="text-sm font-medium text-gray-700">
              Select All ({(filteredProducts || []).length} products)
            </label>
            {(selectedProducts || []).length > 0 && (
              <span className="ml-4 text-sm text-blue-600">
                {(selectedProducts || []).length} selected
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Products List */}
      <Card>
        <div className="p-4">
          <h3 className="text-lg font-semibold mb-4">Products</h3>
          
          {loading ? (
            <div className="text-center py-8">
              <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
              <p className="mt-2 text-gray-600">Loading products...</p>
            </div>
          ) : (filteredProducts || []).length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-600">
                {searchTerm ? "No products found matching your search." : "No products available."}
              </p>
            </div>
          ) : (
            <div className="grid gap-3 max-h-96 overflow-y-auto">
              {(filteredProducts || []).map((product) => (
                <div
                  key={product?.id || Math.random()}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <Checkbox
                    checked={(selectedProducts || []).includes(product?.id)}
                    onChange={(e) => handleProductSelection(product?.id, e.checked)}
                  />
                  <div className="flex-1">
                  
                    <h4 className="font-medium text-gray-800">SKU{(product?.id || "") + ": " + (product?.title || "")}</h4>
                    {product?.description && (
                      <p className="text-sm text-gray-600 truncate">{product.description}</p>
                    )}
                    <div className="flex gap-4 mt-1">
                      {product?.category && (
                        <span className="text-xs text-blue-600">Category: {product.category}</span>
                      )}
                      {product?.price && (
                        <span className="text-xs text-green-600">Price: Rs{product.price}</span>
                      )}
                      
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Submit Button */}
      <div className="mt-4 flex justify-end">
        <Button
          label={`Submit Selection (${(selectedProducts || []).length} products)`}
          icon="pi pi-check"
          onClick={handleSubmit}
          disabled={!(textName || "").trim() || (selectedProducts || []).length === 0}
          className="p-primary-btn"
        />
      </div>
    </div>
  );
} 